/**
 * Os números da base, no topo da lista de clientes (26/10/2026): o total, quem
 * está sem responsável, quem está esfriando e quem tem oportunidade aberta.
 *
 * Cada cartão é também o atalho para o recorte dele: o número diz onde há
 * trabalho, e o clique leva até lá. São as mesmas contagens das abas, que a API
 * manda sem o recorte aplicado — por isso não mudam ao trocar de recorte.
 *
 * Sem seta de tendência ("↑ 12%"): a base não guarda a contagem de ontem, e uma
 * variação inventada diria uma coisa que ninguém mediu.
 */

import { type ReactNode } from 'react';
import { DIAS_PARA_ESFRIAR, type ContagensDeClientes } from '@cliqcrm/contracts';
import { type FiltroDaLista } from '@/entities/clientes/cliente';
import { Icone, type NomeDoIcone } from '@/shared/ui';

const CARTOES: readonly {
  readonly recorte: FiltroDaLista['recorte'];
  readonly rotulo: string;
  readonly apoio: string;
  readonly icone: NomeDoIcone;
  readonly tom: 'roxo' | 'atencao' | 'info' | 'ok';
}[] = [
  {
    recorte: 'todos',
    rotulo: 'Total de clientes',
    apoio: 'Base de contatos',
    icone: 'clientes',
    tom: 'roxo',
  },
  {
    recorte: 'sem_responsavel',
    rotulo: 'Sem responsável',
    apoio: 'Precisam de atenção',
    icone: 'alerta',
    tom: 'atencao',
  },
  {
    recorte: 'esfriando',
    rotulo: 'Esfriando',
    apoio: `Negócio aberto e ${DIAS_PARA_ESFRIAR} dias sem contato`,
    icone: 'relogio',
    tom: 'info',
  },
  {
    recorte: 'com_negocio',
    rotulo: 'Oportunidades abertas',
    apoio: 'Clientes em negociação',
    icone: 'funil',
    tom: 'ok',
  },
];

export function NumerosDaBase({
  contagens,
  recorte,
  aoEscolher,
}: {
  /** `null` enquanto a primeira página não chegou: o cartão mostra "—". */
  contagens: ContagensDeClientes | null;
  recorte: FiltroDaLista['recorte'];
  aoEscolher: (recorte: FiltroDaLista['recorte']) => void;
}): ReactNode {
  return (
    <div className="clientes__numeros" role="group" aria-label="Números da base">
      {CARTOES.map((cartao) => (
        <button
          key={cartao.recorte}
          type="button"
          className="clientes__numero"
          data-tom={cartao.tom}
          aria-pressed={recorte === cartao.recorte}
          onClick={() => aoEscolher(cartao.recorte)}
        >
          <span className="clientes__numero-icone" aria-hidden="true">
            <Icone nome={cartao.icone} />
          </span>
          <span className="clientes__numero-texto">
            <span className="clientes__numero-rotulo">{cartao.rotulo}</span>
            <span className="clientes__numero-valor">
              {contagens ? contagens[cartao.recorte].toLocaleString('pt-BR') : '—'}
            </span>
            <span className="clientes__numero-apoio">{cartao.apoio}</span>
          </span>
        </button>
      ))}
    </div>
  );
}
